import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, BookOpen, Star, ShieldCheck } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
const CHECKOUT_LINK = '#checkout-placeholder'; // Placeholder as requested
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function LandingPage() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gradient-to-b from-[#fdf2f8] via-[#fff0f5] to-[#fae8ff] overflow-hidden" data-id="element-20">
      <PurchaseNotification data-id="element-21" />

      {/* Navigation / Header */}
      <nav className="fixed top-0 w-full z-40 bg-white/80 backdrop-blur-md border-b border-pink-100 py-3 px-4" data-id="element-22">
        <div className="max-w-4xl mx-auto flex justify-between items-center" data-id="element-23">
          <span className="font-serif font-bold text-xl text-pink-900" data-id="element-24">
            La soledad que no se nota
          </span>
          <button onClick={scrollToPurchase} className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-all shadow-md hover:shadow-lg" data-id="element-25">
            Acceder ahora
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="pt-28 pb-16 px-4 relative" data-id="element-26">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 opacity-30" data-id="element-27">
          <div className="absolute top-20 left-10 w-64 h-64 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob" data-id="element-28"></div>
          <div className="absolute top-20 right-10 w-64 h-64 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" data-id="element-29"></div>
          <div className="absolute -bottom-8 left-20 w-64 h-64 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" data-id="element-30"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center" data-id="element-31">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} className="mb-8 relative inline-block group cursor-pointer" onClick={scrollToPurchase} data-id="element-32">
            <div className="absolute -inset-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" data-id="element-33"></div>
            <img src="https://cdn.magicpatterns.com/uploads/5jdQu9nfmF9u9tZphfWxFv/ChatGPT_Image_6_ene_2026,_01_41_42.png" alt="La soledad que no se nota - Libro Digital" className="relative rounded-lg shadow-2xl w-full max-w-sm mx-auto transform transition duration-500 hover:scale-[1.02]" data-id="element-34" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} className="text-4xl md:text-6xl font-serif font-bold text-gray-900 mb-4 leading-tight" data-id="element-35">
            La soledad que no se nota
          </motion.h1>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="text-xl md:text-2xl text-pink-800 italic mb-8" data-id="element-36">
            Cuando estás acompañada, pero te sentís sola
          </motion.p>

          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={scrollToPurchase} className="bg-gradient-to-r from-pink-600 to-purple-600 text-white text-lg px-8 py-4 rounded-full font-bold shadow-lg hover:shadow-pink-500/30 transition-all flex items-center gap-2 mx-auto" data-id="element-37">
            Quiero dejar de sentirme así <ArrowRight className="w-5 h-5" data-id="element-38" />
          </motion.button>
        </div>
      </header>

      {/* Main Copy Section - The Emotional Journey */}
      <main className="max-w-2xl mx-auto px-6 py-12 space-y-16" data-id="element-39">
        {/* The Hook */}
        <motion.section variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="prose prose-lg prose-pink mx-auto text-gray-700" data-id="element-40">
          <motion.p variants={fadeIn} className="font-semibold text-xl text-purple-900" data-id="element-41">
            Estás acompañada.
          </motion.p>
          <motion.p variants={fadeIn} data-id="element-42">Tenés mensajes.</motion.p>
          <motion.p variants={fadeIn} data-id="element-43">Tenés conversaciones.</motion.p>
          <motion.p variants={fadeIn} data-id="element-44">
            Tal vez incluso tenés a alguien al lado.
          </motion.p>

          <motion.div variants={fadeIn} className="my-8 pl-4 border-l-4 border-pink-300 italic text-gray-600" data-id="element-45">
            <p data-id="element-46">Y sin embargo…</p>
            <p data-id="element-47">cuando se apaga el ruido,</p>
            <p data-id="element-48">cuando termina el día,</p>
            <p data-id="element-49">cuando nadie te habla,</p>
            <p className="font-bold text-pink-800" data-id="element-50">te sentís sola.</p>
          </motion.div>
        </motion.section>

        {/* The Definition */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="bg-white/50 backdrop-blur-sm p-8 rounded-2xl shadow-sm border border-white" data-id="element-51">
          <motion.p variants={fadeIn} className="text-lg mb-4" data-id="element-52">
            No es una soledad evidente.
          </motion.p>
          <motion.p variants={fadeIn} className="text-lg mb-4" data-id="element-53">
            No es la que se cuenta.
          </motion.p>
          <motion.p variants={fadeIn} className="text-lg mb-4" data-id="element-54">
            No es la que se justifica.
          </motion.p>
          <motion.p variants={fadeIn} className="text-xl font-serif font-bold text-purple-900 mb-6" data-id="element-55">
            Es la que se disimula.
          </motion.p>

          <motion.div variants={fadeIn} className="space-y-4 text-gray-700" data-id="element-56">
            <p data-id="element-57">Porque, en teoría, todo está bien.</p>
            <p data-id="element-58">Y eso es lo que más confunde.</p>
            <p data-id="element-59">Tenés vínculos.</p>
            <p data-id="element-60">Tenés rutinas.</p>
            <p data-id="element-61">Tenés una vida que funciona.</p>
            <p className="font-medium text-pink-700" data-id="element-62">
              Pero hay algo adentro que no se siente acompañado.
            </p>
          </motion.div>
        </motion.section>

        {/* The Feeling */}
        <motion.section initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} transition={{
        duration: 1
      }} viewport={{
        once: true
      }} className="text-center py-8" data-id="element-63">
          <h3 className="text-2xl font-serif text-gray-800 mb-6" data-id="element-64">
            No es tristeza. No es depresión. No es aburrimiento.
          </h3>
          <p className="text-3xl font-serif text-pink-800 italic" data-id="element-65">
            Es un vacío silencioso.
          </p>
          <div className="mt-8 space-y-2 text-gray-600" data-id="element-66">
            <p data-id="element-67">Difícil de explicar.</p>
            <p data-id="element-68">Fácil de esconder.</p>
            <p className="font-medium" data-id="element-69">
              Una soledad que no viene del abandono,
            </p>
            <p className="font-bold" data-id="element-70">sino de la desconexión.</p>
          </div>
        </motion.section>

        {/* The Invisible Loneliness */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} data-id="element-71">
          <motion.h2 variants={fadeIn} className="text-3xl font-serif font-bold text-purple-900 mb-8 text-center" data-id="element-72">
            La soledad que nadie ve
          </motion.h2>

          <div className="grid gap-4" data-id="element-73">
            {['No es estar sola en una casa vacía.', 'Es estar con alguien y no sentirse vista.', 'Es hablar y sentir que nadie escucha de verdad.', 'Es compartir tiempo, pero no presencia.', 'Es estar… sin sentirse habitada.'].map((text, i) => <motion.div key={i} variants={fadeIn} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-74">
                <div className="mt-1 min-w-[20px] h-[20px] rounded-full bg-pink-100 flex items-center justify-center" data-id="element-75">
                  <div className="w-2 h-2 rounded-full bg-pink-500" data-id="element-76"></div>
                </div>
                <p className="text-gray-700" data-id="element-77">{text}</p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* The Guilt */}
        <motion.section initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-3xl border border-purple-100" data-id="element-78">
          <h3 className="text-2xl font-serif font-bold text-purple-900 mb-6" data-id="element-79">
            Y lo más doloroso no es sentirla. Es la culpa.
          </h3>

          <div className="space-y-4 italic text-gray-600 border-l-2 border-purple-200 pl-6 mb-8" data-id="element-80">
            <p data-id="element-81">“¿Cómo puedo sentirme así si no estoy sola?”</p>
            <p data-id="element-82">“¿Qué me falta agradecer?”</p>
            <p data-id="element-83">“¿Qué tengo de mal?”</p>
          </div>

          <div className="space-y-3 text-gray-700" data-id="element-84">
            <p data-id="element-85">Entonces te callás.</p>
            <p data-id="element-86">Te adaptás.</p>
            <p data-id="element-87">Seguís funcionando.</p>
            <p data-id="element-88">Cumplís. Respondés. Sostenés.</p>
            <p className="font-bold text-lg text-pink-700" data-id="element-89">
              Pero no te acompañás.
            </p>
          </div>
        </motion.section>

        {/* The Reality Check */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="space-y-6" data-id="element-90">
          <motion.p variants={fadeIn} data-id="element-91">Nadie sospecha lo que te pasa.</motion.p>
          <motion.p variants={fadeIn} data-id="element-92">
            Te reís en reuniones y volvés a casa con un nudo.
          </motion.p>
          <motion.p variants={fadeIn} data-id="element-93">
            Dormís con alguien y te sentís sola.
          </motion.p>
          <motion.p variants={fadeIn} data-id="element-94">
            Tenés conversaciones, pero no conexión.
          </motion.p>
          <motion.p variants={fadeIn} className="font-medium text-lg" data-id="element-95">
            Estás rodeada… y emocionalmente aislada.
          </motion.p>

          <motion.div variants={fadeIn} className="bg-white p-6 rounded-xl shadow-md my-8" data-id="element-96">
            <p className="mb-4 text-gray-600" data-id="element-97">
              Y empezás a preguntarte en silencio:
            </p>
            <ul className="space-y-2 italic text-purple-800" data-id="element-98">
              <li data-id="element-99">“¿Será que soy demasiado sensible?”</li>
              <li data-id="element-100">“¿Será que pido demasiado?”</li>
              <li data-id="element-101">“¿Será que esta sensación es así para siempre?”</li>
            </ul>
          </motion.div>

          <motion.div variants={fadeIn} className="text-center" data-id="element-102">
            <p className="text-4xl font-serif font-bold text-pink-600 mb-4" data-id="element-103">
              No.
            </p>
            <p className="text-lg" data-id="element-104">No es que seas demasiado.</p>
            <p className="text-lg" data-id="element-105">No es que estés rota.</p>
            <p className="text-lg" data-id="element-106">No es que te falte amor.</p>
          </motion.div>
        </motion.section>

        {/* The Truth */}
        <motion.section className="bg-purple-900 text-white p-10 rounded-3xl shadow-xl relative overflow-hidden" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-107">
          <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20" data-id="element-108"></div>

          <h2 className="text-3xl font-serif font-bold mb-6 relative z-10" data-id="element-109">
            La verdad incómoda
          </h2>

          <div className="space-y-6 relative z-10 text-purple-100" data-id="element-110">
            <p data-id="element-111">No te sentís sola porque estés sola.</p>
            <p className="text-xl font-medium text-white" data-id="element-112">
              Te sentís sola porque aprendiste a vincularte sin habitarte.
            </p>
            <p data-id="element-113">Nadie te enseñó a acompañarte emocionalmente.</p>
            <p data-id="element-114">Nadie te mostró cómo estar con otros sin abandonarte.</p>
            <p data-id="element-115">Nadie te explicó que podías sentirte vacía incluso rodeada.</p>
            <div className="pt-4 border-t border-purple-700" data-id="element-116">
              <p data-id="element-117">
                Entonces buscás afuera lo que nunca aprendiste a construir
                adentro.
              </p>
              <p className="mt-2" data-id="element-118">
                Y cuando eso no alcanza, la soledad aparece.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Why nothing worked */}
        <motion.section variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-119">
          <motion.h3 variants={fadeIn} className="text-2xl font-serif font-bold text-gray-900 mb-6" data-id="element-120">
            Por qué nada de lo que probaste funcionó
          </motion.h3>
          <motion.p variants={fadeIn} className="mb-4" data-id="element-121">
            Tal vez intentaste entenderlo en terapia. Leíste libros. Buscaste
            respuestas en frases de amor propio. Probaste más vínculos, más
            planes, más actividad.
          </motion.p>
          <motion.p variants={fadeIn} className="mb-6 font-medium" data-id="element-122">
            Y aun así, el vacío volvió.
          </motion.p>
          <motion.div variants={fadeIn} className="bg-pink-50 p-6 rounded-lg border-l-4 border-pink-400" data-id="element-123">
            <p className="mb-4" data-id="element-124">
              Porque todas esas soluciones confundieron soledad con falta de
              gente.
            </p>
            <p className="mb-4" data-id="element-125">Atacaron el síntoma, no la raíz.</p>
            <p data-id="element-126">Nunca explicaron por qué te sentís sola incluso acompañada.</p>
          </motion.div>
          <motion.p variants={fadeIn} className="mt-8 text-xl text-center font-serif" data-id="element-127">
            La verdadera soledad no es externa.{' '}
            <span className="font-bold text-purple-800" data-id="element-128">Es interna.</span>
            <br data-id="element-129" />
            Es no sentirse emocionalmente habitada.
          </motion.p>
        </motion.section>

        {/* The Revelation */}
        <motion.section initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center space-y-4" data-id="element-130">
          <h3 className="text-2xl font-serif font-bold text-gray-800" data-id="element-131">
            El momento en que todo se reveló
          </h3>
          <p data-id="element-132">No fue una ruptura. No fue una pérdida. No fue una tragedia.</p>
          <p className="italic" data-id="element-133">Fue una noche común.</p>
          <p data-id="element-134">
            Mensajes sin profundidad. Conversaciones vacías. Una sensación
            imposible de explicar.
          </p>
          <div className="py-6" data-id="element-135">
            <p className="text-lg font-medium" data-id="element-136">Ahí apareció la verdad:</p>
            <p className="text-2xl font-serif text-pink-700 mt-2" data-id="element-137">
              No faltaba nadie. Faltaba alguien adentro.
            </p>
          </div>
        </motion.section>

        {/* The Solution */}
        <motion.section className="bg-white p-8 rounded-3xl shadow-xl border border-pink-100" initial={{
        scale: 0.95,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-138">
          <div className="text-center mb-8" data-id="element-139">
            <p className="text-sm uppercase tracking-widest text-gray-500 mb-2" data-id="element-140">
              Esta guía existe por eso
            </p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-purple-900 mb-2" data-id="element-141">
              La soledad que no se nota
            </h2>
            <p className="text-pink-600 italic" data-id="element-142">
              Cuando estás acompañada, pero te sentís sola
            </p>
          </div>

          <div className="space-y-4 text-gray-700 mb-8" data-id="element-143">
            <div className="flex items-center gap-3" data-id="element-144">
              <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-145"></div>
              <p data-id="element-146">No es un libro motivacional.</p>
            </div>
            <div className="flex items-center gap-3" data-id="element-147">
              <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-148"></div>
              <p data-id="element-149">No es autoayuda vacía.</p>
            </div>
            <div className="flex items-center gap-3" data-id="element-150">
              <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-151"></div>
              <p data-id="element-152">No te dice que “no necesites a nadie”.</p>
            </div>
          </div>

          <div className="bg-purple-50 p-6 rounded-xl text-center" data-id="element-153">
            <p className="font-bold text-purple-900 mb-2" data-id="element-154">
              Te enseña algo mucho más real:
            </p>
            <p className="text-lg" data-id="element-155">
              Cómo acompañarte emocionalmente para poder estar con otros sin
              perderte.
            </p>
          </div>
        </motion.section>

        {/* What you'll find */}
        <motion.section variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-156">
          <h3 className="text-2xl font-serif font-bold text-gray-900 mb-8 text-center" data-id="element-157">
            Qué vas a encontrar acá
          </h3>
          <div className="grid gap-4" data-id="element-158">
            {['Entender por qué te sentís sola incluso cuando no lo estás', 'Identificar tu tipo de soledad', 'Reconectar con tu mundo interno', 'Aprender a estar con otros sin abandonarte', 'Construir una sensación real de compañía interna'].map((item, i) => <motion.div key={i} variants={fadeIn} className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm border border-pink-50" data-id="element-159">
                <BookOpen className="w-6 h-6 text-pink-500 flex-shrink-0" data-id="element-160" />
                <p className="text-gray-800" data-id="element-161">{item}</p>
              </motion.div>)}
          </div>

          <div className="mt-10 text-center space-y-2 text-gray-700" data-id="element-162">
            <p data-id="element-163">
              No para aislarte. No para volverte fría. No para dejar de amar.
            </p>
            <p className="font-bold text-xl text-purple-800" data-id="element-164">
              Sino para dejar de desaparecer en los vínculos.
            </p>
          </div>
        </motion.section>

        {/* Transformation & Promise */}
        <motion.section className="grid md:grid-cols-2 gap-8" data-id="element-165">
          <div className="bg-white p-6 rounded-2xl shadow-sm" data-id="element-166">
            <h4 className="font-serif font-bold text-xl text-purple-900 mb-4 flex items-center gap-2" data-id="element-167">
              <Star className="w-5 h-5 text-yellow-500" data-id="element-168" /> La transformación
            </h4>
            <ul className="space-y-3 text-sm" data-id="element-169">
              <li className="flex gap-2" data-id="element-170">
                <span className="text-green-500" data-id="element-171">✓</span> Poder estar sola sin
                angustia.
              </li>
              <li className="flex gap-2" data-id="element-172">
                <span className="text-green-500" data-id="element-173">✓</span> Poder estar con otros
                sin vacío.
              </li>
              <li className="flex gap-2" data-id="element-174">
                <span className="text-green-500" data-id="element-175">✓</span> Elegir vínculos desde
                plenitud, no desde carencia.
              </li>
              <li className="flex gap-2" data-id="element-176">
                <span className="text-green-500" data-id="element-177">✓</span> Sentirte acompañada
                incluso cuando nadie está cerca.
              </li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm" data-id="element-178">
            <h4 className="font-serif font-bold text-xl text-purple-900 mb-4 flex items-center gap-2" data-id="element-179">
              <Heart className="w-5 h-5 text-pink-500" data-id="element-180" /> La promesa
            </h4>
            <p className="text-sm mb-3 font-medium" data-id="element-181">
              Podés dejar de sentirte sola:
            </p>
            <ul className="space-y-2 text-sm text-gray-600 mb-4" data-id="element-182">
              <li data-id="element-183">• sin cambiar de pareja,</li>
              <li data-id="element-184">• sin alejarte de nadie,</li>
              <li data-id="element-185">• sin volverte emocionalmente distante.</li>
            </ul>
            <p className="font-bold text-pink-700" data-id="element-186">
              Solo dejando de abandonarte.
            </p>
          </div>
        </motion.section>
      </main>

      {/* Bonuses Section */}
      <section className="bg-purple-900 py-20 px-4 text-white overflow-hidden relative" data-id="element-187">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20" data-id="element-188">
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-pink-500 rounded-full mix-blend-overlay filter blur-3xl" data-id="element-189"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-190">
          <div className="text-center mb-12" data-id="element-191">
            <span className="bg-pink-500 text-white px-4 py-1 rounded-full text-sm font-bold tracking-wider mb-4 inline-block" data-id="element-192">
              REGALO EXCLUSIVO
            </span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4" data-id="element-193">
              Bonos Gratuitos
            </h2>
            <p className="text-purple-200 text-lg" data-id="element-194">
              Disponibles solo por el día de hoy con tu compra
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-195">
            {/* Bonus 1 */}
            <motion.div initial={{
            x: -50,
            opacity: 0
          }} whileInView={{
            x: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} className="group cursor-pointer" onClick={scrollToPurchase} data-id="element-196">
              <div className="relative" data-id="element-197">
                <div className="absolute -inset-2 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl blur opacity-30 group-hover:opacity-60 transition duration-500" data-id="element-198"></div>
                <img src="https://cdn.magicpatterns.com/uploads/o5Vu9xBZVifmnAWiwuh5eq/ChatGPT_Image_6_ene_2026,_02_03_02.png" alt="Test: Tu tipo de soledad invisible" className="relative rounded-xl shadow-2xl w-full transform transition duration-500 group-hover:-translate-y-2" data-id="element-199" />
              </div>
              <div className="mt-6 text-center md:text-left" data-id="element-200">
                <h3 className="text-2xl font-bold mb-2 text-pink-300" data-id="element-201">
                  Bono #1: Test de Soledad
                </h3>
                <p className="text-gray-300" data-id="element-202">
                  Descubrí exactamente qué tipo de soledad estás experimentando
                  y cómo sanarla específicamente.
                </p>
              </div>
            </motion.div>

            {/* Bonus 2 */}
            <motion.div initial={{
            x: 50,
            opacity: 0
          }} whileInView={{
            x: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} className="group cursor-pointer" onClick={scrollToPurchase} data-id="element-203">
              <div className="relative" data-id="element-204">
                <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl blur opacity-30 group-hover:opacity-60 transition duration-500" data-id="element-205"></div>
                <img src="https://cdn.magicpatterns.com/uploads/65zthYQaDXwZcGhEunEfTX/ChatGPT_Image_6_ene_2026,_02_05_12.png" alt="Calendario Emocional" className="relative rounded-xl shadow-2xl w-full transform transition duration-500 group-hover:-translate-y-2" data-id="element-206" />
              </div>
              <div className="mt-6 text-center md:text-left" data-id="element-207">
                <h3 className="text-2xl font-bold mb-2 text-pink-300" data-id="element-208">
                  Bono #2: Calendario Emocional
                </h3>
                <p className="text-gray-300" data-id="element-209">
                  Un año habitándote. Una herramienta visual para registrar y
                  validar tus emociones día a día.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-b from-purple-50 to-white" data-id="element-210">
        <div className="max-w-6xl mx-auto" data-id="element-211">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-gray-900 mb-16" data-id="element-212">
            Mujeres que empezaron a habitarse
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-213">
            <TestimonialCard name="Lucía" age={32} location="Buenos Aires" text="Siempre pensé que el problema era mi pareja. Leía este libro y lloraba porque me di cuenta que yo me había ido de mí misma mucho antes. Ahora puedo estar con él sin sentir ese vacío horrible." delay={0} data-id="element-214" />
            <TestimonialCard name="Martina" age={28} location="Córdoba" text="Es increíble cómo describe exactamente lo que siento. Esa soledad estando rodeada de gente... nadie me entendía. Esta guía fue el abrazo que necesitaba para dejar de culparme." delay={0.1} data-id="element-215" />
            <TestimonialCard name="Sofía" age={41} location="Rosario" text="Tengo hijos, marido, trabajo, amigas. Y me sentía sola. La culpa me mataba. Entender que era desconexión interna cambió todo. El calendario emocional es mi herramienta favorita." delay={0.2} data-id="element-216" />
            <TestimonialCard name="Valentina" age={35} location="Mendoza" text="No es el típico libro de autoayuda que te dice 'amate a vos misma'. Te explica CÓMO hacerlo. Cómo volver a casa cuando estás perdida en los demás. Gracias por esto." delay={0.3} data-id="element-217" />
            <TestimonialCard name="Carolina" age={29} location="La Plata" text="El test me voló la cabeza. Descubrí que mi soledad era por 'adaptación excesiva'. Siempre siendo lo que otros querían. Empecé a ser yo y la soledad desapareció." delay={0.4} data-id="element-218" />
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      <section id="purchase-section" className="py-20 px-4 bg-white relative overflow-hidden" data-id="element-219">
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-220">
          <div className="mb-8" data-id="element-221">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4" data-id="element-222">
              Empezá a acompañarte hoy
            </h2>
            <p className="text-xl text-gray-600" data-id="element-223">
              Todo lo que necesitás para volver a vos
            </p>
          </div>

          <div className="bg-gradient-to-b from-white to-purple-50 border-2 border-pink-100 rounded-3xl p-8 shadow-2xl relative" data-id="element-224">
            <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-xl uppercase tracking-wider" data-id="element-225">
              Oferta Especial
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center mb-8" data-id="element-226">
              <div className="w-full md:w-1/3" data-id="element-227">
                <img src="https://cdn.magicpatterns.com/uploads/5jdQu9nfmF9u9tZphfWxFv/ChatGPT_Image_6_ene_2026,_01_41_42.png" alt="Pack Completo" className="rounded-lg shadow-lg transform rotate-[-5deg] hover:rotate-0 transition duration-300" data-id="element-228" />
              </div>
              <div className="w-full md:w-2/3 text-left space-y-3" data-id="element-229">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-230">
                  La soledad que no se nota
                </h3>
                <div className="space-y-2" data-id="element-231">
                  <div className="flex items-center gap-2 text-gray-700" data-id="element-232">
                    <BookOpen className="w-5 h-5 text-pink-500" data-id="element-233" />
                    <span data-id="element-234">Guía Digital Completa (PDF)</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700" data-id="element-235">
                    <Star className="w-5 h-5 text-pink-500" data-id="element-236" />
                    <span data-id="element-237">
                      Bono #1: Test de Soledad Invisible{' '}
                      <span className="text-green-600 text-sm font-bold ml-1" data-id="element-238">
                        GRATIS
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700" data-id="element-239">
                    <Star className="w-5 h-5 text-pink-500" data-id="element-240" />
                    <span data-id="element-241">
                      Bono #2: Calendario Emocional{' '}
                      <span className="text-green-600 text-sm font-bold ml-1" data-id="element-242">
                        GRATIS
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8" data-id="element-243">
              <div className="flex flex-col items-center" data-id="element-244">
                <p className="text-gray-500 line-through text-lg mb-1" data-id="element-245">
                  $29.800
                </p>
                <div className="text-5xl font-bold text-pink-600 mb-2" data-id="element-246">
                  $14.900{' '}
                  <span className="text-lg text-gray-500 font-normal" data-id="element-247">ARS</span>
                </div>

                <motion.a href={CHECKOUT_LINK} whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="w-full md:w-auto bg-green-500 hover:bg-green-600 text-white text-xl font-bold py-4 px-12 rounded-full shadow-lg hover:shadow-green-500/30 transition-all flex items-center justify-center gap-2 mb-4" data-id="element-248">
                  👉 Accedé ahora a la guía
                </motion.a>

                <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-249">
                  <ShieldCheck className="w-4 h-4" data-id="element-250" />
                  <span data-id="element-251">Pago 100% seguro y encriptado</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Emotional Close */}
      <section className="py-20 px-4 bg-[#fdf2f8] text-center" data-id="element-252">
        <div className="max-w-2xl mx-auto space-y-6" data-id="element-253">
          <h3 className="text-2xl font-serif font-bold text-gray-800" data-id="element-254">
            Si esta sensación te resulta familiar
          </h3>
          <div className="italic text-gray-600 space-y-2" data-id="element-255">
            <p data-id="element-256">
              Si alguna vez pensaste “no me falta gente, me falta algo adentro”…
            </p>
            <p data-id="element-257">
              Si sentís que nadie nota esta soledad, pero vos la sentís todos
              los días…
            </p>
          </div>
          <p className="font-bold text-purple-900 text-lg" data-id="element-258">
            Entonces esta guía es para vos.
          </p>

          <div className="pt-8 space-y-4" data-id="element-259">
            <p className="text-xl font-serif" data-id="element-260">
              Leé <span className="font-bold" data-id="element-261">La soledad que no se nota</span>.
            </p>
            <p data-id="element-262">
              No para cambiar tu vida.{' '}
              <span className="font-bold text-pink-700" data-id="element-263">
                Sino para habitarla.
              </span>
            </p>
            <p data-id="element-264">
              No para dejar de necesitar a otros.{' '}
              <span className="font-bold text-pink-700" data-id="element-265">
                Sino para empezar a acompañarte de verdad.
              </span>
            </p>
          </div>

          <div className="pt-8" data-id="element-266">
            <button onClick={scrollToPurchase} className="text-pink-600 font-bold hover:text-pink-800 underline decoration-2 underline-offset-4 transition-colors" data-id="element-267">
              Quiero mi copia ahora
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 border-t border-gray-100 text-center text-gray-400 text-sm" data-id="element-268">
        <p data-id="element-269">
          © {new Date().getFullYear()} La soledad que no se nota. Todos los
          derechos reservados.
        </p>
      </footer>
    </div>;
}